import { centroid } from "@turf/turf"

/**
 * @typedef {Array<Array<number>>} FloorPlanCorners
 * Four corner coordinates in [lng, lat] format:
 * [top-left, top-right, bottom-right, bottom-left]
 */

/**
 * Translates floor plan corner coordinates by applying a fan offset.
 * Floor plan coordinates are stored relative to the base building position.
 * When floors fan out, this applies the same offset to position the image
 * on the fanned floor polygon.
 *
 * @param {FloorPlanCorners|null} coordinates - 4 corner coordinates in [lng, lat] format
 * @param {{lng: number, lat: number}} offset - Fan pattern offset to apply
 * @returns {FloorPlanCorners|null} Translated coordinates or null if invalid input
 */
export function translateFloorPlanCoordinates(coordinates, offset) {
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length !== 4) {
    return null
  }

  return coordinates.map(([lng, lat]) => [lng + offset.lng, lat + offset.lat])
}

/**
 * Computes the offset between an original floor polygon centroid and a fan floor polygon centroid.
 * This offset is applied to floor plan coordinates to position them on the fanned floor.
 * Floor plan coordinates are placed relative to the original floor position, so the offset
 * must be computed from the original floor (not the building) to the fanned position.
 *
 * @param {Object|null} originalFloorPolygon - GeoJSON Polygon geometry of the original floor
 * @param {Object|null} fanFloorPolygon - GeoJSON Polygon geometry of the fanned floor
 * @returns {{lng: number, lat: number}} The offset to apply
 */
export function computeFanOffset(originalFloorPolygon, fanFloorPolygon) {
  if (!originalFloorPolygon || !fanFloorPolygon) {
    return { lng: 0, lat: 0 }
  }

  const originalCentroid = centroid({ type: "Feature", geometry: originalFloorPolygon })
  const fanCentroid = centroid({ type: "Feature", geometry: fanFloorPolygon })

  const [bLng, bLat] = originalCentroid.geometry.coordinates
  const [fLng, fLat] = fanCentroid.geometry.coordinates

  return {
    lng: fLng - bLng,
    lat: fLat - bLat,
  }
}
